import { useEffect, useState, useRef } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { api, inr, fmtDate, formatApiError, API_URL } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { StatusPill } from "./Quotations";
import { ProformaAiEmailDialog } from "@/components/QuoteAiEmailDialog";
import { SearchCombobox } from "@/components/SearchCombobox";
import { DatePicker } from "@/components/DatePicker";
import { Plus, FileText, MessageCircle, Mail, ArrowRight, Paperclip, IndianRupee, Wallet, Pencil, Factory, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const emptyAdv = () => ({ amount: "", mode: "Bank Transfer", reference: "", date: today() });

export default function Proformas() {
  const [list, setList] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [search, setSearch] = useUrlSearch();
  const [aiEmailFor, setAiEmailFor] = useState(null);
  const [advFor, setAdvFor] = useState(null);
  const [adv, setAdv] = useState(emptyAdv());
  const [advBusy, setAdvBusy] = useState(false);
  const [convAsk, setConvAsk] = useState(null);
  const [convBusy, setConvBusy] = useState(false);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // quotation id when editing existing PI
  const [qsel, setQsel] = useState(null);       // selected source quotation doc
  const [form, setForm] = useState(null);
  const [formAdv, setFormAdv] = useState(emptyAdv());
  const [poFile, setPoFile] = useState(null);
  const [poCheck, setPoCheck] = useState(null);
  const [saving, setSaving] = useState(false);
  const poInputRef = useRef(null);

  const load = () => Promise.all([
    api.get("/quotations/proformas").then((r) => setList(r.data)),
    api.get("/quotations").then((r) => setQuotes(r.data)),
    api.get("/transporters").then((r) => setTransporters(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const buildForm = (q) => ({
    transport: q.transport || "", transporter_id: q.transporter_id || "", transporter_gstin: q.transporter_gstin || "",
    po_reference: q.po_reference || "", po_date: q.po_date ? String(q.po_date).slice(0, 10) : "",
    customer_po_file_id: q.customer_po_file_id || "", customer_po_name: q.customer_po_name || "", customer_po_check: q.customer_po_check || null,
    in_house: !!q.in_house,
    items: (q.items || []).map((it) => ({ ...it })),
    freight: q.freight || 0, terms: q.terms || "", notes: q.notes || "", internal_note: q.internal_note || "",
  });

  const openNew = () => { setEditing(null); setQsel(null); setForm(null); setFormAdv(emptyAdv()); setPoFile(null); setOpen(true); };
  const openEdit = (q) => { setEditing(q.id); setQsel(q); setForm(buildForm(q)); setFormAdv(emptyAdv()); setPoFile(null); setOpen(true); };
  const pickQuote = (qid) => {
    const q = quotes.find((x) => x.id === qid);
    if (!q) return;
    setQsel(q);
    setForm(buildForm(q));
  };

  const totals = (form?.items || []).reduce((acc, it) => {
    const sub = (+it.quantity || 0) * (+it.unit_price || 0);
    acc.sub += sub; acc.gst += sub * (+it.gst || 0) / 100;
    if (form?.in_house) acc.profit += sub;
    else if ((+it.purchase_rate || 0) > 0) {
      acc.cost += (+it.purchase_rate) * (+it.quantity || 0);
      acc.profit += ((+it.unit_price || 0) - (+it.purchase_rate)) * (+it.quantity || 0);
    }
    return acc;
  }, { sub: 0, gst: 0, cost: 0, profit: 0 });
  const freightGst = (+form?.freight || 0) * 0.18;
  const total = totals.sub + totals.gst + freightGst + (+form?.freight || 0);

  const save = async () => {
    if (!qsel || !form) { toast.error("Select a quotation first"); return; }
    setSaving(true);
    try {
      const body = {
        customer_id: qsel.customer_id,
        lead_id: qsel.lead_id || null, lead_number: qsel.lead_number || "",
        date: qsel.date || null, valid_until: qsel.valid_until || null,
        transport: form.transport, transporter_id: form.transporter_id, transporter_gstin: form.transporter_gstin,
        po_reference: form.po_reference, po_date: form.po_date || null,
        items: form.items.map(({ _stock, ...i }) => ({ ...i, quantity: +i.quantity, purchase_rate: +i.purchase_rate || 0, margin_percent: +i.margin_percent || 0, unit_price: +i.unit_price, gst: +i.gst, use_stock: !!i.use_stock, stock_qty: +i.stock_qty || 0, stock_id: i.stock_id || "" })),
        freight: +form.freight, terms: form.terms, notes: form.notes, internal_note: form.internal_note || "",
      };
      await api.put(`/quotations/${qsel.id}`, body);
      let pfNo = qsel.proforma_number;
      if (!editing || !pfNo) {
        const r = await api.post(`/quotations/${qsel.id}/proforma`);
        pfNo = r.data.proforma_number;
      }
      toast.success(`Pro Forma ${pfNo} saved`);
      const qid = qsel.id;
      const pendingPo = poFile;
      const pendingAdv = { ...formAdv };
      setOpen(false); setEditing(null); setQsel(null); setForm(null); setPoFile(null);
      if (pendingPo) {
        toast.info("Verifying customer PO with AI…");
        try {
          const fd = new FormData();
          fd.append("file", pendingPo);
          const r = await api.post(`/quotations/${qid}/customer-po`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          setPoCheck(r.data.check);
        } catch (e) { toast.error(formatApiError(e)); }
      }
      if (+pendingAdv.amount > 0) {
        try {
          await api.post("/payments/advance", { quotation_id: qid, amount: +pendingAdv.amount, mode: pendingAdv.mode, reference: pendingAdv.reference, date: pendingAdv.date || null });
          toast.success(`Advance of ${inr(+pendingAdv.amount)} recorded against Pro Forma ${pfNo}`);
        } catch (e) { toast.error(`Advance failed: ${formatApiError(e)}`); }
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const openAdvance = (q) => { setAdv(emptyAdv()); setAdvFor(q); };
  const saveAdvance = async () => {
    if (!(+adv.amount > 0)) { toast.error("Enter a valid advance amount"); return; }
    setAdvBusy(true);
    try {
      await api.post("/payments/advance", {
        quotation_id: advFor.id, amount: +adv.amount, mode: adv.mode,
        reference: adv.reference, date: adv.date || null,
      });
      toast.success(`Advance of ${inr(+adv.amount)} recorded against Pro Forma ${advFor.proforma_number}`);
      setAdvFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setAdvBusy(false);
  };

  const shareWhatsApp = async (q) => {
    try {
      const r = await api.get(`/quotations/${q.id}/share-link`);
      const link = `${API_URL}/public/quotations/${r.data.share_token}/proforma`;
      const msg = `Dear ${q.customer_snapshot?.company_name || "Customer"},\n\nPlease find our Pro Forma Invoice ${q.proforma_number} against Quotation ${q.number} for ${inr(q.total)}.\nView / download the PDF here: ${link}\n\nKindly arrange the advance payment as per terms.\n\nRegards,\nHi-Tech Silico\n+91 8888837157`;
      const digits = (q.customer_snapshot?.mobile || "").replace(/\D/g, "");
      const phone = digits.length === 10 ? `91${digits}` : digits;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const viewPoFile = async (fid) => {
    try {
      const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open file"); }
  };

  const delPf = async (q) => {
    if (!window.confirm(`Delete Pro Forma ${q.proforma_number}?\nThe quotation ${q.number} will remain and can be re-issued as a new Pro Forma.`)) return;
    try {
      await api.delete(`/quotations/${q.id}/proforma`);
      toast.success(`Pro Forma ${q.proforma_number} deleted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doConvert = async (q) => {
    setConvBusy(true);
    try {
      const r = await api.post(`/quotations/${q.id}/convert`);
      const so = r.data;
      const w = so?.intel_warnings || 0;
      if ((q.advance_total || 0) > 0) {
        toast.success(`Converted to ${so.number} — advance of ${inr(q.advance_total)} linked to the order`);
      } else {
        toast.success(`Converted to Sales Order ${so.number}`);
      }
      if (w > 0) toast.warning(`Order Brain found ${w} item${w > 1 ? "s" : ""} to fix on ${so.number}.`, { duration: 9000 });
      setConvAsk(null);
      load();
      navigate(`/orders?q=${encodeURIComponent(so.number)}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setConvBusy(false);
  };

  const onConvertClick = (q) => {
    if ((q.advance_total || 0) > 0) {
      if (window.confirm(`Convert Pro Forma ${q.proforma_number} to a Sales Order?\nAdvance of ${inr(q.advance_total)} will be linked to the order.`)) doConvert(q);
    } else {
      setConvAsk(q);
    }
  };

  const shown = list.filter((q) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return [q.proforma_number, q.number, q.customer_snapshot?.company_name, q.status]
      .some((f) => (f || "").toLowerCase().includes(s));
  });
  const pager = usePager(shown);
  const eligible = quotes.filter((q) => !q.proforma_number && q.status !== "Converted");

  return (
    <div className="space-y-4" data-testid="proformas-page">
      <PageHeader title="Pro Forma" subtitle="Advance invoices against quotations"
        right={
          <Button onClick={openNew} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="pf-new">
            <Plus size={14} className="mr-1" /> New Pro Forma
          </Button>
        } />

      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PI no., quotation, customer…"
             className="h-9 w-72 bg-white" data-testid="pf-search" />

      <div className="border border-slate-100 rounded-xl bg-white overflow-x-auto shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Pro Forma No</th>
              <th className="text-left px-4 py-2.5 hidden lg:table-cell">Quotation</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Date</th>
              <th className="text-right px-4 py-2.5">Amount</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((q) => (
              <tr key={q.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`pf-row-${q.id}`}>
                <td className="px-4 py-3 font-mono text-slate-700">{q.proforma_number}</td>
                <td className="px-4 py-3 hidden lg:table-cell font-mono text-xs text-slate-500">{q.number}</td>
                <td className="px-4 py-3">{q.customer_snapshot?.company_name || "-"}</td>
                <td className="px-4 py-3 hidden md:table-cell">{fmtDate(q.proforma_date)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{inr(q.total)}</td>
                <td className="px-4 py-3"><StatusPill status={q.status} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <a href={`${API_URL}/quotations/${q.id}/proforma`} target="_blank" rel="noreferrer"
                       className="p-1.5 text-slate-500 hover:text-[#2563EB] rounded hover:bg-slate-100 transition-colors duration-150" title="Pro Forma PDF" data-testid={`pf-pdf-${q.id}`}><FileText size={14} /></a>
                    {q.customer_po_file_id && (
                      <button onClick={() => viewPoFile(q.customer_po_file_id)} className="p-1.5 text-emerald-600 hover:text-emerald-800 rounded hover:bg-emerald-50 transition-colors duration-150" title={`View uploaded Purchase Order${q.po_reference ? ` (${q.po_reference})` : ""}`} data-testid={`pf-po-view-${q.id}`}><Paperclip size={14} /></button>
                    )}
                    <button onClick={() => openAdvance(q)} className="p-1.5 text-amber-600 hover:text-amber-800 rounded hover:bg-amber-50 transition-colors duration-150" title="Record advance payment" data-testid={`pf-advance-btn-${q.id}`}><IndianRupee size={14} /></button>
                    <button onClick={() => shareWhatsApp(q)} className="p-1.5 text-emerald-600 hover:text-emerald-800 rounded hover:bg-emerald-50 transition-colors duration-150" title="Share on WhatsApp" data-testid={`pf-whatsapp-${q.id}`}><MessageCircle size={14} /></button>
                    <button onClick={() => setAiEmailFor(q)} className="p-1.5 text-[#3B82F6] hover:text-[#2563EB] rounded hover:bg-blue-50 transition-colors duration-150" title="AI Email — draft & send with PDF" data-testid={`pf-ai-email-${q.id}`}><Mail size={14} /></button>
                    <button onClick={() => openEdit(q)} className="p-1.5 text-slate-500 hover:text-[#2563EB] rounded hover:bg-slate-100 transition-colors duration-150" title="Edit" data-testid={`pf-edit-${q.id}`}><Pencil size={14} /></button>
                    {q.status !== "Converted" && (
                      <button onClick={() => onConvertClick(q)} className="p-1.5 text-emerald-700 hover:text-emerald-900 rounded hover:bg-emerald-50 transition-colors duration-150" title="Convert to Sales Order" data-testid={`pf-convert-${q.id}`}><ArrowRight size={14} /></button>
                    )}
                    {q.status !== "Converted" && (
                      <button onClick={() => delPf(q)} className="p-1.5 text-slate-500 hover:text-rose-600 rounded hover:bg-rose-50 transition-colors duration-150" title="Delete Pro Forma" data-testid={`pf-del-${q.id}`}><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-sm text-slate-500">{search ? "No pro formas match your search." : "No pro formas yet — create one from a quotation."}</td></tr>}
          </tbody>
        </table>
        <Pager pager={pager} />
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setQsel(null); setForm(null); setPoFile(null); } }}>
        <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-hidden flex flex-col content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
          <DialogHeader><DialogTitle>{editing ? `Edit Pro Forma ${qsel?.proforma_number || ""}` : "New Pro Forma"}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 pl-1 pr-4 space-y-3" data-testid="pf-dialog-body">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Quotation Number *</Label>
                {editing ? (
                  <Input value={`${qsel?.number || ""} — ${qsel?.customer_snapshot?.company_name || ""}`} readOnly className="mt-1.5 bg-slate-50" data-testid="pf-quote-locked" />
                ) : (
                  <SearchCombobox testId="pf-quote" placeholder="Select quotation to auto-fill" searchPlaceholder="Search quotations…" emptyText="No quotations without a pro forma"
                    label={qsel ? `${qsel.number} — ${qsel.customer_snapshot?.company_name || ""}` : ""}
                    options={eligible.map((q) => ({ id: q.id, label: `${q.number} — ${q.customer_snapshot?.company_name || "-"}`, sub: `${fmtDate(q.date)} · ${inr(q.total)}` }))}
                    onSelect={pickQuote} />
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Customer</Label>
                <Input value={qsel?.customer_snapshot?.company_name || ""} readOnly className="mt-1.5 bg-slate-50" data-testid="pf-customer" />
              </div>
            </div>

            {form && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Transporter</Label>
                    <Select value={form.transporter_id} onValueChange={(v) => {
                      const t = transporters.find((x) => x.id === v);
                      setForm({ ...form, transporter_id: v, transport: t?.name || "", transporter_gstin: t?.gstin || "" });
                    }}>
                      <SelectTrigger className="mt-1.5" data-testid="pf-transporter"><SelectValue placeholder="Select transporter" /></SelectTrigger>
                      <SelectContent>
                        {transporters.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        {transporters.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No transporters — add under Transporters</div>}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Customer PO Ref</Label>
                    <Input value={form.po_reference} onChange={(e) => setForm({ ...form, po_reference: e.target.value })} placeholder="e.g. Verbal / PO-1234" className="mt-1.5" data-testid="pf-poref" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Customer PO File (AI verified)</Label>
                    <input ref={poInputRef} type="file" accept=".pdf,image/*" className="hidden"
                           onChange={(e) => setPoFile(e.target.files?.[0] || null)} data-testid="pf-po-file-input" />
                    <div className="flex items-center gap-2 mt-1.5">
                      <button type="button" onClick={() => poInputRef.current?.click()}
                              className="inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md px-3 h-9 text-slate-600 hover:border-slate-400 transition-colors duration-150 min-w-0"
                              data-testid="pf-po-upload-btn">
                        <Paperclip size={12} className="flex-none" />
                        <span className="truncate max-w-[160px]">{poFile ? poFile.name : (form.customer_po_name || "Upload PO")}</span>
                      </button>
                      {form.customer_po_file_id && !poFile && (
                        <button type="button" onClick={() => viewPoFile(form.customer_po_file_id)}
                                className="text-xs text-blue-600 hover:underline flex-none" data-testid="pf-po-view">View</button>
                      )}
                    </div>
                    {form.customer_po_check && !poFile && (
                      <p className={`text-[11px] mt-1 ${form.customer_po_check.match === true ? "text-emerald-600" : form.customer_po_check.match === false ? "text-rose-600" : "text-amber-600"}`} data-testid="pf-po-check-badge">
                        AI check: {form.customer_po_check.match === true ? "✓ matches quotation" : form.customer_po_check.match === false ? `✗ ${form.customer_po_check.issues?.length || 0} mismatch(es)` : "manual check needed"}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Purchase Order Date</Label>
                    <DatePicker value={form.po_date} onChange={(v) => setForm({ ...form, po_date: v })} className="mt-1.5" testId="pf-podate" />
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Products <span className="normal-case font-normal text-slate-400">(from quotation — read-only)</span></p>
                  {form.items.map((it, i) => {
                    const amt = (+it.quantity || 0) * (+it.unit_price || 0);
                    const salesRate = (+it.purchase_rate || 0) > 0 ? Math.round((+it.purchase_rate) * (1 + (+it.margin_percent || 0) / 100)) : 0;
                    const profit = form.in_house ? amt : ((+it.purchase_rate || 0) > 0 ? ((+it.unit_price || 0) - (+it.purchase_rate)) * (+it.quantity || 0) : 0);
                    const Meta = ({ label, value }) => value ? (
                      <span className="inline-flex items-center gap-1 text-xs">
                        <span className="text-slate-400">{label}</span>
                        <span className="text-slate-700 font-medium">{value}</span>
                      </span>
                    ) : null;
                    const Stat = ({ label, value, cls = "text-slate-800" }) => (
                      <div className="min-w-[86px]">
                        <div className="text-[9px] uppercase tracking-widest text-slate-400">{label}</div>
                        <div className={`text-sm font-semibold tabular-nums ${cls}`}>{value}</div>
                      </div>
                    );
                    return (
                      <div key={i} className="border border-slate-200 rounded-lg p-4 bg-white hover:border-slate-300 transition-colors duration-150" data-testid={`pf-item-${i}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {it.product_code && <span className="text-[10px] font-mono font-bold bg-[#13264D] text-white rounded px-1.5 py-0.5">{it.product_code}</span>}
                              <span className="font-semibold text-slate-800 text-sm">{it.description || "-"}</span>
                              {form.in_house && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-medium">
                                  <Factory size={10} /> In-House
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                              <Meta label="Size" value={it.size} />
                              <Meta label="Color" value={it.color} />
                              <Meta label="Cut Length" value={it.cut_length} />
                              <Meta label="Notes" value={it.tc_size} />
                            </div>
                          </div>
                          <div className="text-right flex-none">
                            <div className="text-[9px] uppercase tracking-widest text-slate-400">Amount</div>
                            <div className="font-display font-bold text-slate-900 tabular-nums text-lg leading-tight">{inr(amt)}</div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 pt-3 border-t border-dashed border-slate-200">
                          <Stat label="Quantity" value={`${it.quantity} ${it.unit || ""}`} />
                          {!form.in_house && <Stat label="Purchase ₹" value={it.purchase_rate ? inr(+it.purchase_rate) : "—"} />}
                          {!form.in_house && <Stat label="Margin" value={it.margin_percent ? `${it.margin_percent}%` : "—"} />}
                          <Stat label="Sales Rate" value={form.in_house ? inr(+it.unit_price || 0) : (salesRate ? inr(salesRate) : "—")} />
                          <Stat label={`Price / ${it.unit || "Unit"}`} value={inr(+it.unit_price || 0)} />
                          <Stat label="Profit" value={profit ? inr(profit) : "—"} cls={profit < 0 ? "text-rose-600" : "text-emerald-700"} />
                          <Stat label="GST" value={`${it.gst}%`} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="border border-amber-200 bg-amber-50/50 rounded-md p-3 space-y-2.5" data-testid="pf-form-advance">
                  <div className="flex items-center gap-2">
                    <Wallet size={14} className="text-amber-600" />
                    <p className="text-xs uppercase tracking-widest text-amber-800 font-semibold">Advance Payment</p>
                    {(qsel?.advance_total || 0) > 0 && (
                      <span className="text-[11px] text-emerald-700 font-medium" data-testid="pf-form-advance-existing">Already recorded: {inr(qsel.advance_total)}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Amount ₹</Label>
                      <Input type="number" min="0" placeholder="0" value={formAdv.amount}
                             onChange={(e) => setFormAdv({ ...formAdv, amount: e.target.value })} className="mt-1 h-9 bg-white" data-testid="pf-form-adv-amount" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Mode</Label>
                      <Select value={formAdv.mode} onValueChange={(v) => setFormAdv({ ...formAdv, mode: v })}>
                        <SelectTrigger className="mt-1 h-9 bg-white" data-testid="pf-form-adv-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Bank Transfer", "UPI", "Cheque", "Cash"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Reference / UTR</Label>
                      <Input placeholder="Optional" value={formAdv.reference}
                             onChange={(e) => setFormAdv({ ...formAdv, reference: e.target.value })} className="mt-1 h-9 bg-white" data-testid="pf-form-adv-ref" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Date</Label>
                      <DatePicker value={formAdv.date} onChange={(v) => setFormAdv({ ...formAdv, date: v })} className="mt-1 h-9" testId="pf-form-adv-date" />
                    </div>
                  </div>
                  <p className="text-[11px] text-amber-700">Recorded on save — flows into the Sales Order on conversion, then auto-adjusts into its invoice.</p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Freight (INR)</Label>
                    <Input type="number" value={form.freight} onChange={(e) => setForm({ ...form, freight: e.target.value })} className="mt-1.5" data-testid="pf-freight" />
                  </div>
                  <div className="col-span-2 flex flex-col items-end justify-end text-sm">
                    <div className="w-full max-w-xs space-y-1">
                      <Row l="Subtotal" v={totals.sub} />
                      <Row l={+form.freight > 0 ? "GST (incl. 18% on freight)" : "GST"} v={totals.gst + freightGst} />
                      <Row l="Freight" v={+form.freight || 0} />
                      <Row l="Total" v={total} bold />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Terms & Conditions</Label>
                  <Textarea value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} rows={2} className="mt-1.5" />
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Internal Notepad</Label>
                    <span className="text-[10px] text-amber-600">(private — never shown to client)</span>
                  </div>
                  <Textarea value={form.internal_note || ""} onChange={(e) => setForm({ ...form, internal_note: e.target.value })}
                            rows={2} placeholder="e.g. 2% commission payable to Mr. X on this order"
                            className="mt-1.5 bg-amber-50/60 border-amber-200 focus-visible:ring-amber-300" data-testid="pf-internal-note" />
                </div>
              </>
            )}
            {!form && <p className="text-sm text-slate-400 py-10 text-center">Select a quotation above — its products, customer and terms will load here.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="pf-save">{saving ? "Saving…" : "Save Pro Forma"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!poCheck} onOpenChange={(v) => { if (!v) setPoCheck(null); }}>
        <DialogContent className="max-w-lg" data-testid="pf-po-check-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {poCheck?.match === true
                ? <><CheckCircle2 size={18} className="text-emerald-600" /> Customer PO matches</>
                : poCheck?.match === false
                ? <><AlertTriangle size={18} className="text-rose-600" /> PO mismatch found</>
                : <><AlertTriangle size={18} className="text-amber-500" /> Manual check needed</>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {poCheck?.summary && <p className="text-slate-700" data-testid="pf-po-check-summary">{poCheck.summary}</p>}
            {(poCheck?.issues || []).length > 0 && (
              <ul className="space-y-1.5" data-testid="pf-po-check-issues">
                {poCheck.issues.map((iss, i) => (
                  <li key={i} className="flex items-start gap-2 text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-3 py-2 text-[13px]">
                    <AlertTriangle size={13} className="mt-0.5 flex-none" /> {iss}
                  </li>
                ))}
              </ul>
            )}
            {poCheck?.match === true && <p className="text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2 text-[13px]">All parameters (items, sizes, quantities, rates, totals) match the quotation.</p>}
          </div>
          <DialogFooter>
            <Button onClick={() => setPoCheck(null)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="pf-po-check-close">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!advFor} onOpenChange={(v) => !v && setAdvFor(null)}>
        <DialogContent className="max-w-md" data-testid="pf-advance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wallet size={16} className="text-amber-600" /> Advance Payment — Pro Forma {advFor?.proforma_number}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {advFor?.customer_snapshot?.company_name || "-"} · Quotation {advFor?.number} · {inr(advFor?.total || 0)}
            {(advFor?.advance_total || 0) > 0 && <span className="block text-emerald-700 font-medium mt-0.5">Already recorded: {inr(advFor.advance_total)}</span>}
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Amount ₹</Label>
              <Input type="number" min="0" placeholder="0" value={adv.amount}
                     onChange={(e) => setAdv({ ...adv, amount: e.target.value })} className="mt-1" data-testid="pf-adv-amount" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Mode</Label>
              <Select value={adv.mode} onValueChange={(v) => setAdv({ ...adv, mode: v })}>
                <SelectTrigger className="mt-1" data-testid="pf-adv-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Bank Transfer", "UPI", "Cheque", "Cash"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Reference / UTR</Label>
              <Input placeholder="Optional" value={adv.reference}
                     onChange={(e) => setAdv({ ...adv, reference: e.target.value })} className="mt-1" data-testid="pf-adv-ref" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Date</Label>
              <DatePicker value={adv.date} onChange={(v) => setAdv({ ...adv, date: v })} className="mt-1" testId="pf-adv-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvFor(null)} data-testid="pf-adv-skip">Cancel</Button>
            <Button onClick={saveAdvance} disabled={advBusy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="pf-adv-save">
              {advBusy ? "Saving…" : "Save Advance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!convAsk} onOpenChange={(v) => !v && setConvAsk(null)}>
        <DialogContent className="max-w-md" data-testid="pf-convask-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRight size={16} className="text-emerald-600" /> Convert Pro Forma {convAsk?.proforma_number}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700">
            No advance payment is recorded against this Pro Forma. Would you like to record one before converting?
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConvAsk(null)} data-testid="pf-convask-cancel">Cancel</Button>
            <Button variant="outline" onClick={() => doConvert(convAsk)} disabled={convBusy} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="pf-convask-no">
              {convBusy ? "Converting…" : "No — convert anyway"}
            </Button>
            <Button onClick={() => { openAdvance(convAsk); setConvAsk(null); }} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="pf-convask-yes">
              Yes — record advance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProformaAiEmailDialog quote={aiEmailFor} onClose={() => setAiEmailFor(null)} />
    </div>
  );
}

function Row({ l, v, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-display font-bold text-slate-900 border-t border-slate-300 pt-1 mt-1" : "text-slate-600"}`}>
      <div>{l}</div><div className="tabular-nums">{inr(v)}</div>
    </div>
  );
}
